import React, { useState, useRef, useEffect, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/apiRouting';
import { supportApi, SupportMessage } from '../lib/support-api';
import './AIChatAssistant.css';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  functionExecuted?: string;
}

interface AIChatAssistantProps {
  isOpen: boolean;
  onClose: () => void;
}

const SESSION_KEY = 'bso_support_session';

interface SessionData {
  conversationId: string;
  sessionToken: string;
}

function getStoredSession(): SessionData | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function storeSession(data: SessionData): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
}

function clearStoredSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
}

const AIChatAssistant: React.FC<AIChatAssistantProps> = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [needsEmail, setNeedsEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'active' | 'escalated' | 'resolved'>('idle');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { getIdToken, user } = useAuth();

  // Check auth status on mount
  useEffect(() => {
    async function checkAuth() {
      try {
        const token = await getIdToken();
        if (token) {
          setIsAuthenticated(true);
        }
      } catch {
        // Not authenticated
      }
    }
    checkAuth();
  }, [getIdToken]);

  // Restore session when chat opens
  useEffect(() => {
    if (!isOpen) return;

    const stored = getStoredSession();
    if (stored) {
      setConversationId(stored.conversationId);
      setSessionToken(stored.sessionToken);
      loadConversation(stored.conversationId, stored.sessionToken);
    } else if (isAuthenticated) {
      startConversation();
    } else {
      setNeedsEmail(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when ready
  useEffect(() => {
    if (isOpen && !needsEmail && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, needsEmail, status]);

  async function loadConversation(convId: string, token: string) {
    setIsLoading(true);
    try {
      const result = await supportApi.getConversation(convId, token);
      if (result.success && result.data) {
        setMessages(result.data.messages.map((m, i) => ({
          id: `restored-${i}`,
          role: m.role,
          content: m.content,
          timestamp: new Date(m.timestamp),
        })));
        setStatus(result.data.status as typeof status);
      } else {
        // Session expired or invalid -- start fresh
        clearStoredSession();
        setConversationId(null);
        setSessionToken(null);
        if (isAuthenticated) {
          startConversation();
        } else {
          setNeedsEmail(true);
        }
      }
    } catch {
      clearStoredSession();
      setConversationId(null);
      setSessionToken(null);
      // Fall back to legacy AI chat
      setMessages([{
        id: '1',
        role: 'assistant',
        content: 'Hi! I\'m your BigSystemOne AI Assistant. I can help you with:\n\n- Menu management (update prices, view items)\n- Store information and settings\n- General restaurant operations\n\nJust ask me naturally, like "change the burger price to $8.99" or "show me all menu items".',
        timestamp: new Date()
      }]);
      setStatus('active');
    } finally {
      setIsLoading(false);
    }
  }

  async function startConversation(userEmail?: string) {
    setIsLoading(true);
    setNeedsEmail(false);
    setError(null);
    try {
      const result = await supportApi.startConversation(
        userEmail || undefined,
        sessionToken || undefined
      );
      if (result.success && result.data) {
        setConversationId(result.data.conversationId);
        setSessionToken(result.data.sessionToken);
        setMessages(result.data.messages.map((m, i) => ({
          id: `init-${i}`,
          role: m.role,
          content: m.content,
          timestamp: new Date(m.timestamp),
        })));
        setStatus('active');
        storeSession({
          conversationId: result.data.conversationId,
          sessionToken: result.data.sessionToken,
        });
      } else {
        throw new Error(result.error?.message || 'Failed to start conversation');
      }
    } catch (err) {
      console.error('Failed to start support conversation:', err);
      // Fall back to legacy AI chat mode
      setMessages([{
        id: '1',
        role: 'assistant',
        content: 'Hi! I\'m your BigSystemOne AI Assistant. I can help you with:\n\n- Menu management (update prices, view items)\n- Store information and settings\n- General restaurant operations\n\nJust ask me naturally, like "change the burger price to $8.99" or "show me all menu items".',
        timestamp: new Date()
      }]);
      setStatus('active');
    } finally {
      setIsLoading(false);
    }
  }

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    // If we have a conversationId, use the support API
    if (conversationId) {
      try {
        const result = await supportApi.sendMessage(
          conversationId,
          userMessage.content,
          sessionToken || undefined
        );
        if (result.success && result.data) {
          setMessages(prev => [...prev, {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: result.data!.message.content,
            timestamp: new Date(result.data!.message.timestamp),
          }]);
        } else {
          setMessages(prev => [...prev, {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: result.error?.message || 'Sorry, something went wrong. Please try again.',
            timestamp: new Date(),
          }]);
        }
      } catch {
        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: 'Connection error. Please check your internet and try again.',
          timestamp: new Date(),
        }]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Fallback: use legacy AI chat API
    try {
      const token = await getIdToken();

      const conversationHistory = messages.slice(-10).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await fetch(API_ENDPOINTS.aiChatSend(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: inputMessage,
          conversationHistory
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
        functionExecuted: data.functionExecuted
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');

      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleEmailSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    startConversation(email.trim());
  };

  const handleEscalate = async () => {
    if (!conversationId) return;
    setIsLoading(true);
    try {
      const result = await supportApi.escalate(
        conversationId,
        'User requested human support',
        sessionToken || undefined
      );
      if (result.success && result.data) {
        setStatus('escalated');
        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: result.data!.message,
          timestamp: new Date(),
        }]);
      }
    } catch {
      setError('Failed to escalate. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewConversation = () => {
    clearStoredSession();
    setConversationId(null);
    setSessionToken(null);
    setMessages([]);
    setStatus('idle');
    setError(null);
    if (isAuthenticated) {
      startConversation();
    } else {
      setNeedsEmail(true);
    }
  };

  const formatMessageContent = (content: string) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br />');
  };

  const userMessageCount = messages.filter(m => m.role === 'user').length;
  const canEscalate = conversationId && userMessageCount >= 3 && status === 'active';

  if (!isOpen) return null;

  return (
    <div className="ai-chat-overlay">
      <div className="ai-chat-container">
        <div className="ai-chat-header">
          <div className="ai-chat-title">
            <span className="ai-chat-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.09 8.26L19 7L18.74 13.74L15 12L13.09 15.74L12 22L10.91 15.74L9 12L5.26 13.74L5 7L10.91 8.26L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            </span>
            <div>
              <h3>AI Assistant</h3>
              <span className="ai-chat-status-text">
                {status === 'escalated' ? 'Escalated to human support' :
                 status === 'resolved' ? 'Resolved' :
                 'AI-powered assistance'}
              </span>
            </div>
          </div>
          <div className="ai-chat-controls">
            <button onClick={handleNewConversation} className="ai-chat-clear-btn" title="New conversation">
              New
            </button>
            <button onClick={onClose} className="ai-chat-close-btn" title="Close chat">
              ✕
            </button>
          </div>
        </div>

        <div className="ai-chat-messages">
          {needsEmail ? (
            <div className="ai-chat-email-container">
              <form onSubmit={handleEmailSubmit} className="ai-chat-email-form">
                <p className="ai-chat-email-prompt">
                  Enter your email to start a support conversation.
                </p>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="ai-chat-email-input"
                  autoFocus
                />
                <button type="submit" className="ai-chat-email-submit">
                  Start Chat
                </button>
              </form>
            </div>
          ) : messages.length === 0 && isLoading ? (
            <div className="ai-chat-loading-center">
              <div className="ai-chat-typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div key={message.id} className={`ai-chat-message ${message.role}`}>
                  <div className="ai-chat-message-content">
                    <div
                      className="ai-chat-message-text"
                      dangerouslySetInnerHTML={{
                        __html: formatMessageContent(message.content)
                      }}
                    />
                    {message.functionExecuted && (
                      <div className="ai-chat-function-indicator">
                        <span>Executed: {message.functionExecuted}</span>
                      </div>
                    )}
                  </div>
                  <div className="ai-chat-message-time">
                    {message.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="ai-chat-message assistant">
                  <div className="ai-chat-message-content">
                    <div className="ai-chat-typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="ai-chat-error">
                  {error}
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Escalation and status actions */}
        {!needsEmail && (status === 'escalated' || status === 'resolved') && (
          <div className="ai-chat-status-bar">
            <button onClick={handleNewConversation} className="ai-chat-new-conversation-btn">
              Start a new conversation
            </button>
          </div>
        )}

        {/* Input area */}
        {!needsEmail && status === 'active' && (
          <div className="ai-chat-input-container">
            {canEscalate && (
              <div className="ai-chat-escalate-row">
                <button
                  onClick={handleEscalate}
                  disabled={isLoading}
                  className="ai-chat-escalate-btn"
                >
                  Talk to a human
                </button>
              </div>
            )}
            <div className="ai-chat-input-wrapper">
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="ai-chat-input"
                rows={1}
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="ai-chat-send-btn"
              >
                {isLoading ? '...' : 'Send'}
              </button>
            </div>
            {messages.length <= 1 && (
              <div className="ai-chat-suggestions">
                <button
                  onClick={() => setInputMessage('show me all menu items')}
                  className="ai-chat-suggestion"
                >
                  Show menu items
                </button>
                <button
                  onClick={() => setInputMessage('I need help with my account')}
                  className="ai-chat-suggestion"
                >
                  Account help
                </button>
                <button
                  onClick={() => setInputMessage('show me store information')}
                  className="ai-chat-suggestion"
                >
                  Store info
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIChatAssistant;
